import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { updateFinancialProfile } from '@/actions/financialProfileActions'
import {
  getFinancialProfile,
  getFreeTrialStatus,
  selectProductDescription,
} from '@/selectors/user.selectors'
import {
  useFetchResponse,
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  JOURNEY_SELECTIONS,
  SIGNUP_PATHS,
  STARTER_PLAN_EXPERIMENT_SELECTIONS,
  isFreeTrialEnabled,
} from '../helpers'
import { useAnalyticsView } from '@/features/amplitude'
import { selectCanOptIntoPreviousTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { TypographyStyles } from '@/styles'
import { Button, Separator, Text } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { SignupHeader } from '../SignupHeader'
import { JourneySelection } from './JourneySelection'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import {
  fetchEligibleStripeProducts,
  PlanTier,
} from '@/actions/settings/billingActions'
import { getProductPermissions } from '@/actions/permissionActions'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { FinancialProfile } from '@/reducers/auth/userReducer'
import { TierSelection } from './TierSelection'

export const ChoosePlan = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const [loading, setLoading] = useState(false)
  const pageView = useAnalyticsView()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const freeTrialEnabled = isFreeTrialEnabled(
    freeTrialSearchParamValue,
    userFreeTrialStatus,
    useBasicTypePlan
  )
  const financialProfile = useReselector(getFinancialProfile)
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )
  const eligiblePlans = useFetchResponse(fetchEligibleStripeProducts)
  const eligibleStarterPlans = useMemo(
    () =>
      eligiblePlans?.filter((plan) => plan.metadata?.tier === PlanTier.starter),
    [eligiblePlans]
  )
  const eligibleStandardPlans = useMemo(
    () =>
      eligiblePlans?.filter(
        (plan) =>
          plan.metadata?.tier === PlanTier.essential ||
          plan.metadata?.tier === PlanTier.premium
      ),
    [eligiblePlans]
  )
  const standardPlanTier = useMemo(
    () => eligibleStandardPlans?.[0]?.metadata?.tier,
    [eligibleStandardPlans]
  )
  const starterPlanIds = useMemo(
    () => eligibleStarterPlans?.map((plan) => plan.id) || [],
    [eligibleStarterPlans]
  )
  const starterPlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: starterPlanIds,
  })
  // essential or premium
  const standardPlanIds = useMemo(
    () => eligibleStandardPlans?.map((plan) => plan.id) || [],
    [eligibleStandardPlans]
  )
  const standardPlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: standardPlanIds,
  })
  const [planTier, setPlanTier] = useState<string | undefined>(
    localStorage.getItem('planTier') ?? undefined
  )
  const enableStarterPlans = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStarterPlans,
    false
  )
  const starterPlanDetails = useReselector(selectProductDescription, {
    sections: ['bookkeeping', 'reporting', 'support'],
    isStarterPlanEnabled: enableStarterPlans,
    permissions: starterPlanPermissions?.[starterPlanIds[0]],
  })
  // essential or premium
  const standardPlanDetails = useReselector(selectProductDescription, {
    sections: ['taxes', 'scorp', 'group', 'support'],
    isStarterPlanEnabled: enableStarterPlans,
    permissions: standardPlanPermissions?.[standardPlanIds[0]],
  })

  useEffect(() => {
    pageView('sign up practice journey')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  const [selection, setSelection] = useState(
    financialProfile?.practiceType || ''
  )

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const handleSubmit = async () => {
    setLoading(true)
    let res: FinancialProfile | boolean | undefined
    if (enableStarterPlans) {
      if (planTier) {
        localStorage.setItem('planTier', planTier)
        res = true
      }
    } else {
      res = await updateFinancialProfile(financialProfile?.id, {
        practiceType: selection,
        practiceSize: selection === 'group' ? '2-10' : '1',
      })(dispatch)
    }
    setLoading(false)

    /* If free trial, skip tax */
    if (res) {
      if (freeTrialSearchParamValue || useBasicTypePlan) {
        navigate(SIGNUP_PATHS.choosePlan)
      } else {
        if (canOptIntoPreviousYearTaxes) {
          navigate(SIGNUP_PATHS.previousYearTax)
        } else {
          navigate(SIGNUP_PATHS.choosePlan)
        }
      }
    }
  }

  return (
    <div className="container mx-auto pt-16">
      <div className="flex flex-col space-y-6">
        <span ref={scrollRef} />
        <SignupHeader currentStep={2} />

        {freeTrialEnabled && <FreeTrialSignupBanner />}

        <Text variant={TypographyStyles.H1}>
          Where are you in your private practice journey?
        </Text>

        <Text variant={TypographyStyles.BODY_LG}>
          Choose the option that is closest to your current practice.
        </Text>

        {enableStarterPlans ? (
          <div className="flex flex-col md:flex-row gap-4 flex justify-between">
            <TierSelection
              {...STARTER_PLAN_EXPERIMENT_SELECTIONS['starter']}
              setSelection={setPlanTier}
              selection={planTier}
              details={starterPlanDetails}
              isStarterPlan
            />
            <TierSelection
              {...STARTER_PLAN_EXPERIMENT_SELECTIONS[
                standardPlanTier === PlanTier.premium ? 'premium' : 'essential'
              ]}
              setSelection={setPlanTier}
              selection={planTier}
              details={standardPlanDetails}
            />
          </div>
        ) : (
          <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
            <JourneySelection
              {...JOURNEY_SELECTIONS['gettingStarted']}
              setSelection={setSelection}
              selection={selection}
            />

            <JourneySelection
              {...JOURNEY_SELECTIONS['soloPractice']}
              setSelection={setSelection}
              selection={selection}
            />

            <JourneySelection
              {...JOURNEY_SELECTIONS['groupPractice']}
              setSelection={setSelection}
              selection={selection}
            />
          </div>
        )}

        <Separator className="my-3" />

        {!isMobile && (
          <div className="flex justify-between mb-6">
            <Button
              variant="secondary"
              onClick={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
            >
              Back
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={(!selection && !planTier) || loading}
              id="btn-signup-practice-type"
              loading={loading}
            >
              Continue
            </Button>
          </div>
        )}

        {isMobile && (
          <div className="flex flex-col space-y-4">
            <Button
              onClick={handleSubmit}
              disabled={(!selection && !planTier) || loading}
              id="btn-signup-practice-type"
              loading={loading}
            >
              Continue
            </Button>

            <Button
              variant="secondary"
              onClick={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
            >
              Back
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
